import React, { useState } from 'react'
import propTypes from 'prop-types'
import MomentUtils from '@date-io/moment'
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import { ButtonBase, Menu, MenuItem } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ScheduleIcon from '@material-ui/icons/Schedule'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import LocalShippingIcon from '@material-ui/icons/LocalShipping'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'

import { availabilityOptions, getShipmentStatusLabel, LineAvailability, ShipmentStatus } from '../../../store/helpers/sparePartSaleLineAvailability'

/**
 * Pills for the Availability / ETA and Shipment Status cells of the Spare Part Sales lines table
 * (IS2-177). The look follows the design the Spare Part Sales area approved: a tinted pill with
 * an icon for each value, and the ETA as a small tag under the Availability pill. Processing
 * (IS2-240) gets its own tint so it never reads as Backordered.
 */
const useStyles = makeStyles(() => ({
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		minHeight: 26,
		padding: '2px 10px',
		borderRadius: 14,
		fontSize: 12,
		fontWeight: 500,
		lineHeight: 1.2,
		whiteSpace: 'nowrap',
		border: '1px solid transparent',
		'& svg': {
			fontSize: 16
		}
	},
	backordered: {
		backgroundColor: '#FDEBD3',
		borderColor: '#F5B461',
		color: '#9A5300'
	},
	processing: {
		backgroundColor: '#EDE7FB',
		borderColor: '#A48BE0',
		color: '#5B3BA8'
	},
	released: {
		backgroundColor: '#E3F5EA',
		// Same green as the icon: without an outline the pale fill barely shows against the white row.
		borderColor: '#2EAD5B',
		color: '#1E7A45',
		'& svg': {
			color: '#2EAD5B'
		}
	},
	notShipped: {
		backgroundColor: '#DCDCDC',
		color: '#6B6B6B'
	},
	inTransit: {
		backgroundColor: '#E2ECFE',
		color: '#2556C9'
	},
	delivered: {
		backgroundColor: '#22A04B',
		color: '#FFFFFF'
	},
	editable: {
		cursor: 'pointer',
		'&:hover': {
			filter: 'brightness(0.96)'
		}
	},
	dropDown: {
		marginLeft: -2,
		marginRight: -6
	},
	etaTag: {
		display: 'inline-block',
		marginTop: 4,
		padding: '2px 8px',
		borderRadius: 6,
		backgroundColor: '#FDE7C8',
		color: '#4A3000',
		fontSize: 11,
		fontWeight: 600,
		lineHeight: 1.4,
		whiteSpace: 'nowrap'
	}
}))

const AVAILABILITY_STYLE = {
	[LineAvailability.BACKORDERED]: { className: 'backordered', Icon: ScheduleIcon },
	[LineAvailability.PROCESSING]: { className: 'processing', Icon: AutorenewIcon },
	[LineAvailability.RELEASED]: { className: 'released', Icon: CheckCircleIcon }
}

const SHIPMENT_STYLE = {
	[ShipmentStatus.NOT_SHIPPED]: { className: 'notShipped', Icon: null },
	[ShipmentStatus.IN_TRANSIT]: { className: 'inTransit', Icon: LocalShippingIcon },
	[ShipmentStatus.DELIVERED]: { className: 'delivered', Icon: CheckCircleIcon }
}

/**
 * The availability pill. When the line can be adjusted, the pill opens a menu with every value;
 * otherwise it is plain text.
 */
export const AvailabilityBadge = ({ availability, isEditable, onChange }) => {
	const classes = useStyles()
	const [anchor, setAnchor] = useState(null)
	const { className, Icon } = AVAILABILITY_STYLE[availability]
	const content = (
		<>
			<Icon />
			{availability}
		</>
	)

	if (!isEditable) {
		return <span className={`${classes.pill} ${classes[className]}`}>{content}</span>
	}

	const handleSelect = (value) => {
		setAnchor(null)
		if (value !== availability) onChange(value)
	}

	return (
		<>
			<ButtonBase
				aria-haspopup='true'
				aria-label={`Availability: ${availability}`}
				className={`${classes.pill} ${classes[className]} ${classes.editable}`}
				onClick={(event) => setAnchor(event.currentTarget)}
			>
				{content}
				<ArrowDropDownIcon className={classes.dropDown} />
			</ButtonBase>
			<Menu anchorEl={anchor} keepMounted={false} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
				{availabilityOptions.map((option) => (
					<MenuItem key={option.value} selected={option.value === availability} onClick={() => handleSelect(option.value)}>
						{option.label}
					</MenuItem>
				))}
			</Menu>
		</>
	)
}

AvailabilityBadge.propTypes = {
	availability: propTypes.oneOf(Object.values(LineAvailability)).isRequired,
	isEditable: propTypes.bool,
	onChange: propTypes.func
}

/**
 * The Parts ETA as a small tag. Editable users open a date picker from it. A line without ETA shows
 * nothing, except an outlined placeholder to editable users when the part is still backordered or
 * processing -- a released part has nothing to wait for.
 */
export const EtaTag = ({ value, isEditable, canSetMissing, onChange }) => {
	const classes = useStyles()
	if (!value && !(isEditable && canSetMissing)) return null

	const renderTag = (fieldProps) => {
		const text = fieldProps.value ? `ETA ${fieldProps.value}` : 'ETA'
		const tagClassName = classes.etaTag
		if (!isEditable) return <span className={tagClassName}>{text}</span>
		return (
			<ButtonBase aria-label='Parts ETA' className={`${tagClassName} ${classes.editable}`} onClick={fieldProps.onClick}>
				{text}
			</ButtonBase>
		)
	}

	return (
		<div>
			<MuiPickersUtilsProvider utils={MomentUtils} locale='en'>
				<DatePicker
					value={value || null}
					format='MMM D, YYYY'
					clearable
					clearLabel='CLEAR'
					disabled={!isEditable}
					onChange={(date) => onChange(date)}
					TextFieldComponent={renderTag}
				/>
			</MuiPickersUtilsProvider>
		</div>
	)
}

EtaTag.propTypes = {
	value: propTypes.oneOfType([propTypes.string, propTypes.object]),
	isEditable: propTypes.bool,
	canSetMissing: propTypes.bool,
	onChange: propTypes.func
}

export const ShipmentStatusBadge = ({ shipmentStatus }) => {
	const classes = useStyles()
	const { className, Icon } = SHIPMENT_STYLE[shipmentStatus]
	return (
		<span className={`${classes.pill} ${classes[className]}`}>
			{Icon && <Icon />}
			{getShipmentStatusLabel(shipmentStatus)}
		</span>
	)
}

ShipmentStatusBadge.propTypes = {
	shipmentStatus: propTypes.oneOf(Object.values(ShipmentStatus)).isRequired
}
